/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useEffect, useState } from 'react';
import { Box, Title } from '@mantine/core';

// Lib ui local dependencies
import {
	Select,
	Button,
	Text,
	TextInputV2,
	Divider,
} from '@components/atoms';

// Styles
import classes from './ConversionFactorForm.module.css';

// Utils
import {
	cleanObject,
	convertArrayToLabelValue,
} from '@utils/functions';

// APIs
import { useOrganizationsByProducers } from 'shared/api/get-organizations-by-producers';
import { useOrganizations } from 'shared/api/get-organizations';
import { useEntities } from 'shared/api/get-entities';

// Icons
import useStore from '@store/index';

// I18n
import { DatePicker } from '@components/atoms/DatePicker';
import { filterEntitiesByIDs } from '@features/resources/api-tokens/utils/functions';
import { useTranslation } from 'react-i18next';
import { useSites } from 'shared/api/get-sites';
import { useAddConversionFactor } from '../../../api/create-conversion-factor';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { NumericInput } from '@components/atoms/NumericInput';
import { TSite } from '@global-types/index';
import { getUnitLabel } from '@utils/units';
import { useMediaQuery } from '@mantine/hooks';
import dayjs from 'dayjs';
import { TFormValuesConversionFactor } from '@features/resources/conversion-factors/types';
import { UseFormReturnType } from '@mantine/form';
import { TConversionFactor } from '@global-types/api/api';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ConversionFactorForm = (props: {
	form: UseFormReturnType<TFormValuesConversionFactor>;
	onCancel: () => void;
	onBack: () => void;
}) => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const [
		conversionFactorOrganization,
		setConversionFactorOrganization,
	] = useState('');

	const [
		conversionFactorProducer,
		setConversionFactorProducer,
	] = useState('');

	const {
		conversionFactorsPage,
		conversionFactorsPageSize,
	} = useStore();

	const { t } = useTranslation();

	const { data: organizations } = useOrganizations();
	const { data: projects } = useMeasurabilities({
		entity: conversionFactorProducer
			? conversionFactorProducer
			: [],
	});
	const { data: sites } = useSites();

	const { data: entities } = useEntities({
		organization: conversionFactorOrganization
			? conversionFactorOrganization
			: [],
	});

	const { data: organizationsByProducersIDs } =
		useOrganizationsByProducers();

	const addConversionFactor = useAddConversionFactor(
		conversionFactorsPage,
		conversionFactorsPageSize
	);

	useEffect(() => {
		if (addConversionFactor.isSuccess) {
			props.onBack();
		}
	}, [addConversionFactor.isSuccess, props]);

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const handleSubmitForm = () => {
			props.form.validate();
			if (props.form.isValid()) {
				const { site, ratio, startDate, note } =
					props.form.values;
				const newStartDate =
					dayjs(startDate).format('YYYY-MM-DD');

				const body = {
					site,
					ratio,
					startDate: new Date(newStartDate),
					note,
				};
				addConversionFactor.mutate(cleanObject(body));
			}
		};

		/* ******************************** CONSTANTS ******************************* */

		const requiredFields = [
			'organization',
			'entity',
			'site',
			'project',
			'startDate',
			'note',
		];
		const isValidSelects = requiredFields.every((field) =>
			props.form.isValid(field)
		);
		const isEnableToAdd = isValidSelects;

		const {
			onChange: onChangeProject,
			...organizationProps
		} = props.form.getInputProps('organization');

		const handleChangeOrganization = (_id: string) => {
			props.form.setFieldValue('organization', _id);
			if (_id) setConversionFactorOrganization(_id);
			props.form.setFieldValue('entity', null);
			props.form.setFieldValue('project', null);
			props.form.setFieldValue('site', null);
		};

		const { onChange: onChangeSite, ...entityProps } =
			props.form.getInputProps('entity');

		const handleChangeEntity = (value: string) => {
			if (value) setConversionFactorProducer(value);
			props.form.setFieldValue('entity', value);
			props.form.setFieldValue('project', null);
		};

		const {
			onChange: onChangeProject2,
			...projectProps
		} = props.form.getInputProps('project');

		const handleChangeProject = (value: string) => {
			props.form.setFieldValue('project', value);
			props.form.setFieldValue('site', null);
		};

		const { onChange: onChangeSite2, ...siteProps } =
			props.form.getInputProps('site');

		const handleChangeSite = (value: string) => {
			props.form.setFieldValue('site', value);
		};

		const handleGetRatioUnit = (_siteId: string) => {
			const foundSite = sites.find(
				(_site: TSite) => _site.id === _siteId
			);
			if (foundSite) {
				switch (foundSite.unit) {
					case 'KWH':
						return getUnitLabel('gCO2perKwh');

					case 'MWH':
						return getUnitLabel('gCO2perKwh');

					case 'KG':
						return getUnitLabel('gCO2perKg');

					case 'kg':
						return getUnitLabel('gCO2perKg');

					case 't':
						return getUnitLabel('gCO2perKg');

					case 'T':
						return getUnitLabel('gCO2perKg');

					default:
				}
			} else return '';
		};

		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(
					(values: TFormValuesConversionFactor) => {}
				)}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{t('global.conversionFactor')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('conversionFactors.addConversionFactor')}
					</Text>
				</Box>
				{/* Row for Organization & Entity */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.organization')}
							withAsterisk
							data={convertArrayToLabelValue(
								filterEntitiesByIDs(
									organizations,
									organizationsByProducersIDs
								)
							)}
							placeholder={t('global.selectOrganization')}
							onChange={handleChangeOrganization}
							{...organizationProps}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<Select
							disabled={
								!props.form.isValid('organization')
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.entity')}
							withAsterisk
							data={convertArrayToLabelValue(
								entities.filter(
									(item: TConversionFactor) =>
										item.organization ==
										props.form.getInputProps(
											'organization'
										).value
								)
							)}
							placeholder={t('global.selectEntity')}
							onChange={handleChangeEntity}
							{...entityProps}
						/>
					</Box>
				</Box>
				{/* Row for Project & Site */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled={!props.form.isValid('entity')}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.project')}
							withAsterisk
							data={convertArrayToLabelValue(projects)}
							placeholder={t('global.selectProject')}
							onChange={handleChangeProject}
							{...projectProps}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<Select
							disabled={!props.form.isValid('project')}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.site')}
							withAsterisk
							data={convertArrayToLabelValue(
								sites.filter((item: TSite) =>
									item.project
										? item.project.id ==
											props.form.getInputProps('project')
												.value
										: []
								)
							)}
							placeholder={t('global.selectSite')}
							onChange={handleChangeSite}
							{...siteProps}
						/>
					</Box>
				</Box>
				<Divider size={'xs'} />
				{/* Row for Start date & Ratio */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<DatePicker
							withAsterisk
							clearable
							visibleFrom="xxl"
							size="lg"
							label={t('global.startDate')}
							placeholder={t('global.selectDate')}
							{...props.form.getInputProps('startDate')}
						/>
						<DatePicker
							withAsterisk
							clearable
							hiddenFrom="xxl"
							size="sm"
							label={t('global.startDate')}
							placeholder={t('global.selectDate')}
							{...props.form.getInputProps('startDate')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<NumericInput
							visibleFrom="xxl"
							label={t('global.fcRatio')}
							size={'md'}
							unit={handleGetRatioUnit(
								props.form.getInputProps('site').value
							)}
							step={0.01}
							{...props.form.getInputProps('ratio')}
						/>
						<NumericInput
							hiddenFrom="xxl"
							label={t('global.fcRatio')}
							size={'sm'}
							unit={handleGetRatioUnit(
								props.form.getInputProps('site').value
							)}
							step={0.01}
							{...props.form.getInputProps('ratio')}
						/>
					</Box>
				</Box>

				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInputV2
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.note')}
							placeholder={t('global.note')}
							{...props.form.getInputProps('note')}
						/>
					</Box>
				</Box>
				<Divider size={'xs'} />
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							disabled={!isEnableToAdd}
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={handleSubmitForm}
							loading={addConversionFactor.isLoading}
						>
							{t('conversionFactors.addConversionFactor')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
